import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, DeviceMobile, Heart } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import dalysWebsiteImage from '../../assets/images/dalys-website.png';
import dossierWebsiteImage from '../../assets/images/dossier-website.png';
import vanesch from '../../assets/images/vanesch_boomkwekerij.webp';

const WebsitePortfolio = () => {
  const { t } = useTranslation();

  const portfolioItems = [
    {
      key: 'website1',
      title: 'Dalys AI',
      description: 'Professionele website voor interim management bedrijf met focus op gebruiksvriendelijkheid en conversie.',
      category: 'Business Website',
      image: dalysWebsiteImage,
      technologies: ['React', 'Tailwind CSS', 'Node.js'],
      features: ['Responsive Design', 'SEO Optimized', 'Contact Forms'],
      link: 'https://dalys.ai'
    },
    {
      key: 'website2',
      title: 'Dossier Arbeidsmigranten',
      description: 'Blog platform voor arbeidsmigranten met blog posts en zelf beheerde content.',
      category: 'Blog & News',
      image: dossierWebsiteImage,
      technologies: ['WordPress', 'Newspaper'],
      features: ['Newspaper', 'Blog', 'Mail subscriptions'],
      link: 'https://dossierarbeidsmigranten.nl/'
    },
    {
      key: 'website3',
      title: 'Vanesch Boomkwekerij',
      description: 'Groen en natuurlijke website voor boomkwekerij met product catalogus en bestelling systeem.',
      category: 'Marketing site & Catalog',
      image: vanesch,
      technologies: ['Wordpress', 'Search function'],
      features: ['Product Catalog', 'Inventory Sync'],
      link: 'https://vaneschboomkwekerij.nl/'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const getCategoryIcon = (category) => {
    if (category.includes('E-commerce')) return <Heart className="w-5 h-5" weight="thin" />;
    if (category.includes('Professional')) return <Globe className="w-5 h-5" weight="thin" />;
    return <DeviceMobile className="w-5 h-5" weight="thin" />;
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Onze <span className="text-slate-600">website projecten</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Van eenvoudige bedrijfswebsites tot complexe platforms. 
            Bekijk enkele van onze recente website projecten en laat je inspireren.
          </p>
        </motion.div>

        {/* Portfolio Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
        >
          {portfolioItems.map((item, index) => (
            <motion.div
              key={item.key}
              variants={itemVariants}
              className="group"
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                {/* Project Image */}
                <div className="relative overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 right-4">
                      <a 
                        href={item.link}
                        className="w-full bg-white text-gray-900 py-2 px-4 rounded-lg font-semibold flex items-center justify-center hover:bg-gray-50 transition-colors"
                      >
                        <span>Bekijk Project</span>
                        <ArrowRight className="w-4 h-4 ml-2" weight="thin" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Project Content */}
                <div className="p-8">
                  {/* Category Badge */}
                  <div className="inline-flex items-center bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                    {getCategoryIcon(item.category)}
                    <span className="ml-2">{item.category}</span>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{item.description}</p>

                  {/* Technologies */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Technologieën:</h4>
                    <div className="flex flex-wrap gap-2">
                      {item.technologies.map((tech, techIndex) => (
                        <span 
                          key={techIndex}
                          className="bg-slate-100 text-slate-800 px-3 py-1 rounded-full text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Key Features */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Belangrijkste features:</h4>
                    <div className="space-y-2">
                      {item.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-slate-600 rounded-full mr-3"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Link */}
                  <a 
                    href={item.link} 
                    className="inline-flex items-center text-slate-600 font-semibold hover:text-slate-800 transition-colors group"
                  >
                    <span>Bekijk live website</span>
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" weight="thin" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-3xl p-12 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Wil je ook zo'n website?</h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Laat ons jouw ideeën omzetten naar een professionele website die converteert. 
              Van concept tot live in een paar weken tijd.
            </p>
            <button className="inline-flex items-center bg-gray-900 text-white px-10 py-4 rounded-2xl font-semibold hover:bg-gray-800 transition-colors">
              <span>Start jouw project</span>
              <ArrowRight className="w-5 h-5 ml-2" weight="thin" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WebsitePortfolio;
